import {AbstractFormController, FormState, RemovableSection} from "../../site/lib/AbstractFormController";
import fbPixelHelper from "../../site/fb-pixel-helper";
import {PortedVueFormController} from "../../site/lib/ported-vue-forms-helpers";
import {AccessibleCombobox} from "../../site/component/accessible-combobox";

class HeroFormController extends AbstractFormController {
	readonly controller: PortedVueFormController;
	private readonly workingWithAffiliateSection: RemovableSection|undefined;
	private readonly affiliateSelectionSection: RemovableSection|undefined;

	private lastLocationValue?: string;
	private currentRequestId = 0;
	private locationUpdateDebouncingTimeout?: ReturnType<Window['setTimeout']>;

	constructor(container: HTMLDivElement, form: HTMLFormElement) {
		super(container, form);
		(window as any).testDummy = this;

		this.controller = new PortedVueFormController(form, JSON.parse(container.getAttribute("data-base-form-state") ?? "{data: {}}") as FormState, {
			location: (value, _, event) => {
				clearTimeout(this.locationUpdateDebouncingTimeout);
				this.locationUpdateDebouncingTimeout = undefined;
				if (typeof value === 'string' && value.length) {
					if (value !== this.lastLocationValue) {
						this.toggleSubmitButtonDisabledReason("waiting-for-location-request", true);
						if (!event || event.type.startsWith("formController.") || event.type === 'focusout' || event.type === 'change') {
							this.lastLocationValue = value;
							this.handleLocationUpdate(value);
						} else {
							this.locationUpdateDebouncingTimeout = window.setTimeout(() => {
								this.lastLocationValue = value;
								this.handleLocationUpdate(value);
							}, 250);
						}
					}
				} else {
					this.toggleSubmitButtonDisabledReason("waiting-for-location-request", false);
					this.workingWithAffiliateSection?.remove();
				}
			},
			working_with_affiliate: value => {
				this.affiliateSelectionSection?.toggle(value == '1');
			},
			heard_of_type_id: value => {
				heard_of_other_field?.toggle(value == '19')
			},
			hero_interest_type_id: (value, valueSetter) => {
				const insertionPoint = form.querySelector(".form-group-action-select");
				if (!insertionPoint) {
					return;
				}
				const heroInterestTypeQuestionsWrapper = form.querySelector<HTMLElement>("[data-name='hero_interest_type_questions']");
				if (heroInterestTypeQuestionsWrapper) {
					if (locationDropdownLabel) {
						locationDropdownLabel.innerText = originalLocationDropdownLabelText.replaceAll(/\{\{hero_interest_type_label}}/gi,
							form.querySelector<HTMLInputElement>("input[name='hero_interest_type_id']:checked")?.getAttribute("data-interest-verb") ?? "")
					}
					heroInterestTypeQuestionsWrapper.style.display = value ? '' : 'none';
				}
				for (const [id, questions] of heroInterestTypeIdQuestions.entries()) {
					if (value === id) {
						if (questions.style.display === 'none') {
							questions.style.display = '';
							const updatedNames = new Set<string>();
							for (const input of questions.querySelectorAll("input")) {
								if (input.name && !updatedNames.has(input.name)) {
									updatedNames.add(input.name);
									valueSetter(input.name, this.controller.getInputValue(input.name), true);
								}
							}
						}
					} else {
						if (questions.style.display !== 'none') {
							questions.style.display = 'none';
							const removedNames = new Set<string>();
							for (const input of questions.querySelectorAll("input")) {
								if (input.name && !removedNames.has(input.name)) {
									removedNames.add(input.name);
									valueSetter(input.name, null, true);
								}
							}
						}
					}
				}
			}
		});
		this.controller.setFormStateValue('data.time_zone', new Date().toLocaleDateString('en-US', {day: '2-digit', timeZoneName: 'short'}).slice(4));
		this.controller.setFormStateValue('data.utm_params', this.storeUTMParameters());

		const constantHeardOfOther = this.getCookie('heard_of_other');
		const heard_of_wrapper = form.querySelector('[data-name="heard-of"]');
		const heard_of_wrapper_field = RemovableSection.createOptional(heard_of_wrapper, this, () => !constantHeardOfOther);
		const heard_of_other_field = RemovableSection.createOptional(heard_of_wrapper?.querySelector('[data-name="heard-of_other"]'),
			this, () => !constantHeardOfOther);

		if (constantHeardOfOther) {
			heard_of_wrapper_field?.remove();
			this.controller.setFormStateValue('heard_of_other', this.getCookie('heard_of_other').replace(' ', ''));
			this.controller.setFormStateValue('heard_of_type_id', 19);
		}

		const heroInterestTypeIdQuestions = new Map<string, HTMLElement>();
		for (const input of form.querySelectorAll<HTMLInputElement>("input[name='hero_interest_type_id']")) {
			const questions = form.querySelector<HTMLElement>(`[data-name="hero_interest_type_${input.value}_questions"]`);
			if (questions) {
				heroInterestTypeIdQuestions.set(input.value, questions);
			}
		}
		const value = form.elements.namedItem("location");
		if ((value instanceof HTMLInputElement || value instanceof HTMLSelectElement) && value.parentElement) {
			const ownerThis = this;
			AccessibleCombobox.init(
				value.parentElement,
				{
					suggestionsFetcher(input) {
						const headers: Record<string, string> = {'Accept': 'application/json'};
						if (window.axiosAuth) {
							headers['Authorization'] = `Basic ${btoa(`${window.axiosAuth.username}:${window.axiosAuth.password}`)}`;
						}
						return fetch(`${ownerThis.getBaseURL()}maps/usa/location/${encodeURI(input)}`, {
							method: 'GET', headers, credentials: "include"
						});
					},
					async suggestionsResponseParser(response) {
						const res = ((await response.json()) as {city: string, state_prefix: string, zip_code?: string|undefined}[])
							.map(({city, state_prefix: state, zip_code: zip}) => {
								const value = zip ? `${city}, ${state} ${zip}` : `${city}, ${state}`;
								return {label: value, value};
							});
						value.setCustomValidity(res.length && res.map(option => option.value).includes(value.value)
							? "" : "Please enter either a city and state or a valid zip code");
						return res;
					}
				}
			);
		}
		const locationDropdownLabel = form.querySelector<HTMLLabelElement>("[data-name='hero_interest_type_questions'] label[for='location']");
		let originalLocationDropdownLabelText: string;
		if (locationDropdownLabel) {
			originalLocationDropdownLabelText = locationDropdownLabel.innerText;
		} else {
			originalLocationDropdownLabelText = "";
		}

		this.workingWithAffiliateSection = RemovableSection.createOptional(form.querySelector("[data-name='working_with_affiliate_section']"), this);

		this.affiliateSelectionSection = RemovableSection.createOptional(form.querySelector("[data-name='affiliate_select_section']"), this);

		this.controller.triggerWatches(new Event('formController.setup'));

		if (this.getCookie('refer_affiliate_id')) {
			this.affiliateSelectionSection?.remove();
			this.workingWithAffiliateSection?.remove();
			this.controller.setFormStateValue('refer_affiliate_id', this.getCookie('refer_affiliate_id').replace(' ', ''));
			this.controller.setFormStateValue('working_with_affiliate', "0"); // Apparently, this needs to be 0 when refer_affiliate_id is set
		}
	}

	private handleLocationUpdate(location: string) {
		const myRequestId = ++this.currentRequestId;
		this.doJsonFetch(`/ajax/near-affiliates?location=${encodeURI(location)}`, 'GET')
			.then(response => {
				if (this.currentRequestId !== myRequestId) {
					return response;
				}
				const nearbyAffiliates: {id: number|string, displayName: string}[] = response;
				const adding = this.controller.getFormStateValue('location')
					&& nearbyAffiliates?.length && !this.controller.getFormStateValue('refer_affiliate_id');
				if (this.workingWithAffiliateSection?.toggle(adding)) {
					const dropdown = this.affiliateSelectionSection?.section.querySelector<HTMLSelectElement>("select[name='affiliate_id']");
					if (dropdown) {
						const nearbyAffiliateOptions: HTMLOptionElement[] = [];
						nearbyAffiliateOptions.push(dropdown.options.item(0) ?? document.createElement("option"));

						for (const {id, displayName} of (nearbyAffiliates ?? [])) {
							const option = document.createElement("option");
							option.value = id.toString();
							option.innerText = displayName;
							if (option.value === dropdown.value) {
								option.selected = true;
							}
							nearbyAffiliateOptions.push(option);
						}

						dropdown.replaceChildren(...nearbyAffiliateOptions);
					}
				} else {
					this.controller.setFormStateValue('working_with_affiliate', 0);
				}
				this.toggleSubmitButtonDisabledReason("waiting-for-location-request", false);
				return response;
			})
			.catch(error => console.log(error));
	}

	protected async sendBaseForm() {
		const data: {[key: string]: any} = {
			working_with_affiliate: "0",
			heard_of_type_id: 19,
			heard_of_other: "",
			...this.controller.formState
		};
		if (data['hero_interest_type_id'] !== '1') {
			delete data['data']['plan_to_sell'];
		}
		if (data['hero_interest_type_id'] !== '2') {
			delete data['data']['plan_to_buy'];
		}
		this.doJsonFetch('/ajax/hero-signup', 'post', {'Content-Type': 'application/json'}, JSON.stringify(data))
			.then(response => {
				this.response.success = true;
				this.response.body = response.data;
			})
			.catch(error => {
				console.error(error);
				this.response.success = false;
				//disable submit button and turn on spinner
				this.setSubmitButtonState(false);
				//end of disable submit button and turn on spinner
				const responseData = error.response.data;
				this.response.body = responseData.errors !== undefined ? Object.values(responseData.errors).map((i: any) => i[0]) : [responseData.message];
			})
			.finally(() => {
				fbPixelHelper.triggerHeroCompleteRegistrationEvent(
					this.controller.getFormStateValue("email")?.toString().trim().toLowerCase(),
					this.controller.getFormStateValue("phone")?.toString().replace(/\D/g, '').replace(/^0*/, '1')
				);
				this.handleResponse();
				if (this.response.success) {
					this.displayAffiliatesSplash();
				}
			});
	}

	private displayAffiliatesSplash() {
		const payload: Record<string, string> = {};
		for (const [key, value] of Object.entries(this.response.body)) {
			if (value != null) {
				payload[key] = (value as any).toString();
			}
		}
		if (!payload['campaign_id']) {
			payload['campaign_id'] = this.controller.getFormStateValue('campaign_id')?.toString() ?? '';
		}

		const successPageUrl = this.container.getAttribute('data-success-page-url') ?? '';

		fetch('/wp-json/homes-for-heroes/v1/success-token', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
		})
			.then(resp => resp.json())
			.then((data: { token: string }) => {
				const url = new URL(successPageUrl);
				url.searchParams.set('token', data.token);
				window.location.href = url.toString();
			})
			.catch(err => {
				console.error('Failed to store success token:', err);
				window.location.href = successPageUrl;
			});
	}

	protected getResponseErrorList(): string[] {
		if (!Array.isArray(this.response.body)) {
			console.trace("Response body was not an array of errors.", "Was:", this.response.body);
			return [];
		}
		return this.response.body as string[];
	}
}

for (const container of document.querySelectorAll<HTMLDivElement>("div.wp-block-plaudit-hero-form")) {
	const form = container.querySelector("form");
	if (form) {
		new HeroFormController(container, form);
	}
}
